export class ValueParser {
	/**
	 * Cleans a formatted number string and converts it into a valid numeric string.
	 * Handles both German and English formatted numbers.
	 * @param value The formatted number string to clean.
	 * @returns A valid numeric string or `null` if the input is invalid.
	 */
	public static cleanFormattedValue(value: string): string | null {
		if (typeof value !== "string" || !/^[\d.,-]+$/.test(value)) {
			return null;
		}

		// Determine if the value is in German format (comma as decimal separator)
		const isGermanFormat = value.includes(",") && value.lastIndexOf(",") > value.lastIndexOf(".");

		// Mixed separator check: Ensure there's only one type of decimal separator
		if (value.includes(",") && value.includes(".")) {
			const commaIndex = value.indexOf(",");
			const dotIndex = value.indexOf(".");

			// Mixed separators are valid only in specific formats (e.g., German thousands + decimal)
			if (
				(isGermanFormat && dotIndex > commaIndex) || // Invalid order for German format
				(!isGermanFormat && commaIndex > dotIndex)   // Invalid order for English format
			) {
				return null;
			}
		}

		let cleanedValue = value;

		if (isGermanFormat) {
			// German format: "1.234,56"
			cleanedValue = cleanedValue
				.replace(/\./g, "") // Remove thousands separator
				.replace(",", "."); // Replace decimal separator
		} else {
			// English format: "1,234.56"
			cleanedValue = cleanedValue.replace(/,/g, ""); // Remove thousands separator
		}

		// Ensure there's at most one decimal separator
		if ((cleanedValue.match(/\./g) || []).length > 1) {
			return null;
		}

		// Validate if the resulting value is a valid number
		if (isNaN(parseFloat(cleanedValue))) {
			return null;
		}

		return cleanedValue;
	}
}
